/**
 * The candidates a customer asked Finlay to draft for, kept per job in
 * sessionStorage so "Drafting…" survives leaving the page or reloading.
 * The reach-out worker gives no progress, only an `OutreachGenerated`
 * notification when it finishes (see `useNotificationHub`), and none at all
 * when every draft fails, so the batch also carries when it started.
 */
export interface DraftingBatch {
  /** Candidate ids, not candidate-job ids. */
  ids: string[];
  startedAt: number;
}

/** After this long without drafts, the view stops waiting and says so. */
export const DRAFTING_TIMEOUT_MS = 120_000;

const storageKey = (jobId: string) => `finlay:drafting:${jobId}`;

const isBatch = (value: unknown): value is DraftingBatch =>
  typeof value === 'object' &&
  value !== null &&
  Array.isArray((value as DraftingBatch).ids) &&
  (value as DraftingBatch).ids.every((id) => typeof id === 'string') &&
  typeof (value as DraftingBatch).startedAt === 'number';

export function readDraftingBatch(
  storage: Pick<Storage, 'getItem'>,
  jobId: string,
): DraftingBatch | null {
  try {
    const value: unknown = JSON.parse(storage.getItem(storageKey(jobId)) ?? '');
    return isBatch(value) && value.ids.length > 0 ? value : null;
  } catch {
    return null;
  }
}

export function writeDraftingBatch(
  storage: Pick<Storage, 'setItem' | 'removeItem'>,
  jobId: string,
  batch: DraftingBatch | null,
) {
  try {
    if (batch) storage.setItem(storageKey(jobId), JSON.stringify(batch));
    else storage.removeItem(storageKey(jobId));
  } catch {
    // Storage can be full or blocked; the batch then lives until navigation.
  }
}

/** Adds a new request to a batch still being written; the wait restarts. */
export const mergeDraftingBatch = (
  current: DraftingBatch | null,
  ids: string[],
  now: number,
): DraftingBatch => ({
  ids: [...new Set([...(current?.ids ?? []), ...ids])],
  startedAt: now,
});

/**
 * Every asked-for candidate has a first message. Stages are paginated, so a
 * drafted candidate can move to an Outreach page that is not loaded; a
 * candidate missing from the loaded rows counts as done.
 */
export const isDraftingDone = (
  batch: DraftingBatch,
  rows: { candidate: { id: string }; reachOutMessage?: unknown }[],
) =>
  batch.ids.every((id) => {
    const row = rows.find((candidateJob) => candidateJob.candidate.id === id);
    return !row || Boolean(row.reachOutMessage);
  });
